import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@shared/components/Button/Button';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LockPin } from 'assets/icons/LockPin.svg';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import { CreateSecurityCodeStepsEnum, useCreateSecurityCodeDataContext } from './CreateSecurityCodeContext';
import SecurityCodeModalBase from './SecurityCodeModalBase';
import { SECURITY_CODE_DIGITS_PATTERN } from '../securityCodeConstants';
import SecurityCodeInput from '../SecurityCodeInput/SecurityCodeInput';

type SetupSecurityCodeFormData = {
  securityCode: string;
};

const SetupPasscodeModal = () => {
  const { t } = useTranslation();
  const { setCurrentStep, setPin, close, mode, showSetupDescription, enforced } = useCreateSecurityCodeDataContext();

  const {
    handleSubmit,
    control,
    resetField,
    watch,
    formState: { errors },
  } = useForm<SetupSecurityCodeFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(Schemas.getEnterSecurityCodeValidation()),
    defaultValues: { securityCode: '' },
  });

  const securityCode = watch('securityCode') ?? '';
  const isFormValid = SECURITY_CODE_DIGITS_PATTERN.test(securityCode);

  const onSubmit = (data: SetupSecurityCodeFormData) => {
    setPin(data.securityCode);
    setCurrentStep(CreateSecurityCodeStepsEnum.CONFIRM_PASSCODE);
  };

  const onInvalid = () => {
    resetField('securityCode', { defaultValue: '' });
  };

  const pinError = errors.securityCode?.message;

  const isCreateMode = mode === 'create';

  return (
    <SecurityCodeModalBase
      isVisible
      onClose={close}
      icon={LockPin}
      title={
        isCreateMode ? t('verification.setup_security_code_title') : t('verification.change_security_code_new_title')
      }
      footer={
        <>
          {!enforced && (
            <Button
              variant="secondaryTertiary"
              size="md"
              className="h-12 min-w-[105px] rounded-2xl px-6"
              onClick={close}
            >
              {showSetupDescription ? t('skip_for_now') : t('cancel')}
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            className="h-12 min-w-[157px] rounded-2xl px-6"
            type="submit"
            form="setup-passcode-form"
            disabled={!isFormValid}
          >
            {isCreateMode ? t('verification.set_security_code_cta') : t('verification.change_security_code_new_cta')}
          </Button>
        </>
      }
    >
      {showSetupDescription && (
        <p className="text-center text-sm text-gray-70">
          {t('verification.setup_security_code_description') +
            ' ' +
            (!enforced ? t('verification.setup_security_code_description_if_skip') : '')}
        </p>
      )}
      <form id="setup-passcode-form" onSubmit={handleSubmit(onSubmit, onInvalid)} className="w-full">
        <Controller
          name="securityCode"
          control={control}
          render={({ field }) => (
            <SecurityCodeInput
              value={field.value}
              onChange={field.onChange}
              error={pinError}
              helperText={t('verification.setup_security_code_helper')}
              autoFocus
            />
          )}
        />
      </form>
    </SecurityCodeModalBase>
  );
};

export default SetupPasscodeModal;
